import { auth, onAuthStateChanged } from './firebase';
import { User } from 'firebase/auth';
import {
  JiraProject,
  JiraSprint,
  JiraIssue,
  JiraComment,
  JiraAccessResponse
} from '../types';

/**
 * Cloud Run base URL for the Jira REST API.
 * Reads from environment variables (CRA: REACT_APP_JIRA_API_URL or REACT_APP_JIRA_API_BASE_URL,
 * Vite: VITE_JIRA_API_BASE_URL), falling back to the deployed Cloud Run endpoint.
 */
export const JIRA_API_BASE_URL = (
  process.env.REACT_APP_JIRA_API_URL ||
  process.env.REACT_APP_JIRA_API_BASE_URL ||
  process.env.VITE_JIRA_API_BASE_URL ||
  process.env.VITE_JIRA_API_URL ||
  'https://scrumai-jira-api-2yjg43vusa-el.a.run.app'
).replace(/\/+$/, '');

/**
 * Configured Atlassian Jira site URL.
 */
export const ATLASSIAN_SITE_URL = 'https://scrumaiapp.atlassian.net';

/**
 * Custom Error class for Jira API interactions.
 */
export class JiraApiError extends Error {
  status: number;
  data?: any;

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'JiraApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Waits for Firebase auth to initialize and resolves active Firebase User.
 */
export const waitForFirebaseUser = (timeoutMs = 1500): Promise<User | null> => {
  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise<User | null>((resolve) => {
    let resolved = false;
    let unsub: (() => void) | undefined;

    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        if (typeof unsub === 'function') {
          try {
            unsub();
          } catch {
            // ignore
          }
        }
        resolve(auth.currentUser || null);
      }
    }, timeoutMs);

    unsub = onAuthStateChanged(auth, (user) => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeoutId);
        if (typeof unsub === 'function') {
          try {
            unsub();
          } catch {
            // ignore
          }
        }
        resolve(user);
      }
    });
  });
};

/**
 * Retrieves a fresh Firebase ID token for Jira requests.
 */
export const getJiraIdToken = async (forceRefresh = false): Promise<string> => {
  const user = await waitForFirebaseUser();
  if (!user) {
    throw new JiraApiError('Please sign in to access Jira integration.', 401);
  }

  const token = await user.getIdToken(forceRefresh);
  if (!token) {
    throw new JiraApiError('Authentication token unavailable. Please sign in again.', 401);
  }

  return token;
};

/**
 * Formats user-safe error messages for Jira API errors without exposing internal stack traces.
 */
const formatJiraErrorMessage = (status: number, responseData?: any): string => {
  const serverMsg = responseData?.error || responseData?.message;

  switch (status) {
    case 400:
      return serverMsg || 'Bad request to Jira service. Please verify your query parameters.';
    case 401:
      return serverMsg || 'Your session has expired or Jira authorization is missing. Please sign in.';
    case 403:
      return serverMsg || 'Access denied: You are not an active member of the ScrumAIApp Atlassian organization.';
    case 404:
      return serverMsg || 'Requested Jira resource was not found.';
    case 429:
      return 'Jira API rate limit exceeded. Please wait a moment and try again.';
    default:
      if (status >= 500) {
        return 'A server error occurred connecting to Jira Cloud. Please try again later.';
      }
      return serverMsg || `Jira request failed with status ${status}.`;
  }
};

/**
 * Authenticated HTTP request wrapper for Jira API endpoints.
 */
export async function jiraFetch<T>(
  path: string,
  options: RequestInit = {},
  retryOn401 = true
): Promise<T> {
  const token = await getJiraIdToken();
  const url = `${JIRA_API_BASE_URL}${path}`;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    ...((options.headers as Record<string, string>) || {})
  };

  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    console.log(`[Jira API] ${options.method || 'GET'} ${path}`);
    response = await fetch(url, {
      ...options,
      headers
    });
  } catch (err: any) {
    console.warn(`[Jira API Network Error] ${path}`, err);
    throw new JiraApiError(
      'Network error: unable to connect to the ScrumAI Jira service.',
      0,
      err
    );
  }

  if (response.status === 401 && retryOn401) {
    try {
      const freshToken = await getJiraIdToken(true);
      headers.Authorization = `Bearer ${freshToken}`;
      response = await fetch(url, {
        ...options,
        headers
      });
    } catch {
      // Fall through to error handler
    }
  }

  if (response.ok) {
    console.log(`[Jira API Success] ${path} -> ${response.status}`);
    if (response.status === 204) {
      return {} as T;
    }
    const text = await response.text();
    if (!text) {
      return {} as T;
    }
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  let parsedErrorData: any = null;
  try {
    parsedErrorData = await response.json();
  } catch {
    // Response not JSON
  }

  const message = formatJiraErrorMessage(response.status, parsedErrorData);
  console.warn(`[Jira API Response Error] ${path} -> ${response.status}:`, message);
  throw new JiraApiError(message, response.status, parsedErrorData);
}

/**
 * Verifies whether the signed-in Firebase user has active access to Jira features.
 * GET /jira/me/access
 */
export const checkJiraAccess = async (): Promise<JiraAccessResponse> => {
  const data = await jiraFetch<any>('/jira/me/access');
  return {
    has_access: data?.has_access ?? data?.access ?? data?.is_member ?? true,
    email: data?.email,
    organization: data?.organization || 'ScrumAIApp',
    message: data?.message
  };
};

/**
 * Fetches all Jira projects visible to the service account.
 * GET /jira/my-projects
 */
export const getMyJiraProjects = async (): Promise<JiraProject[]> => {
  const data = await jiraFetch<any>('/jira/my-projects');
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.projects)) {
    return data.projects;
  }
  return [];
};

/**
 * Fetches the active sprints for a specific project.
 * Handles zero, one, or multiple active sprints returned in the sprints array.
 * GET /jira/projects/{project_key}/active-sprint
 */
export const getActiveSprints = async (projectKey: string): Promise<JiraSprint[]> => {
  if (!projectKey) return [];
  try {
    const data = await jiraFetch<any>(
      `/jira/projects/${encodeURIComponent(projectKey)}/active-sprint`
    );
    if (!data) return [];

    let rawList: any[] = [];
    if (Array.isArray(data)) {
      rawList = data;
    } else if (Array.isArray(data?.sprints)) {
      rawList = data.sprints;
    } else if (Array.isArray(data?.active_sprints)) {
      rawList = data.active_sprints;
    } else if (data?.sprint && typeof data.sprint === 'object') {
      rawList = [data.sprint];
    } else if (data?.id || data?.name || data?.sprint_id) {
      rawList = [data];
    }

    return rawList.map((s) => ({
      ...s,
      id: s.id ?? s.sprint_id ?? s.sprintId,
      sprint_id: s.sprint_id ?? s.id ?? s.sprintId
    }));
  } catch (err: any) {
    if (err instanceof JiraApiError && (err.status === 404 || err.status === 204)) {
      return [];
    }
    throw err;
  }
};

/**
 * Fetches the primary active sprint for a specific project.
 * GET /jira/projects/{project_key}/active-sprint
 */
export const getActiveSprint = async (projectKey: string): Promise<JiraSprint | null> => {
  const sprints = await getActiveSprints(projectKey);
  return sprints.length > 0 ? sprints[0] : null;
};

/**
 * Fetches all issues associated with a specific sprint.
 * Normalizes stories array and subtasks returned from backend.
 * GET /jira/sprints/{sprint_id}/issues
 */
export const getSprintIssues = async (sprintId: string | number): Promise<JiraIssue[]> => {
  if (!sprintId) return [];
  const data = await jiraFetch<any>(
    `/jira/sprints/${encodeURIComponent(String(sprintId))}/issues`
  );

  let rawList: any[] = [];
  if (Array.isArray(data)) {
    rawList = data;
  } else if (Array.isArray(data?.stories) && data.stories.length > 0) {
    rawList = data.stories;
  } else if (Array.isArray(data?.issues) && data.issues.length > 0) {
    rawList = data.issues;
  } else if (Array.isArray(data?.sprint_issues)) {
    rawList = data.sprint_issues;
  } else if (Array.isArray(data?.stories)) {
    rawList = data.stories;
  } else if (Array.isArray(data?.issues)) {
    rawList = data.issues;
  }

  return rawList.map((item) => ({
    ...item,
    issueType: item.issue_type || item.issueType || 'Story',
    subtasks: Array.isArray(item.subtasks)
      ? item.subtasks.map((st: any) => ({
          ...st,
          issueType: st.issue_type || st.issueType || 'Sub-task'
        }))
      : []
  }));
};

/**
 * Fetches active work items, optionally filtered by project_key.
 * GET /jira/my-active-work
 * GET /jira/my-active-work?project_key={project_key}
 */
export const getMyActiveWork = async (projectKey?: string): Promise<JiraSprint[]> => {
  const query = projectKey ? `?project_key=${encodeURIComponent(projectKey)}` : '';
  const data = await jiraFetch<any>(`/jira/my-active-work${query}`);
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.sprints)) {
    return data.sprints;
  }
  if (Array.isArray(data?.active_sprints)) {
    return data.active_sprints;
  }
  if (Array.isArray(data?.work_items)) {
    return [{ name: 'Active Sprints', issues: data.work_items }];
  }
  return [];
};

/**
 * Fetches stories and related work items for a given project key.
 * Agrees with backend contract: GET /jira/projects/{project_key}/stories
 * NOTE: The backend does not implement /jira/my-projects/{project_key}/stories.
 */
export const getProjectStories = async (projectKey: string): Promise<JiraIssue[]> => {
  if (!projectKey) {
    throw new JiraApiError('Project key is required', 400);
  }
  const data = await jiraFetch<any>(
    `/jira/projects/${encodeURIComponent(projectKey)}/stories`
  );

  let rawList: any[] = [];
  if (Array.isArray(data)) {
    rawList = data;
  } else if (Array.isArray(data?.stories) && data.stories.length > 0) {
    rawList = data.stories;
  } else if (Array.isArray(data?.issues) && data.issues.length > 0) {
    rawList = data.issues;
  } else if (Array.isArray(data?.stories)) {
    rawList = data.stories;
  } else if (Array.isArray(data?.issues)) {
    rawList = data.issues;
  }

  return rawList.map((item) => ({
    ...item,
    issueType: item.issue_type || item.issueType || 'Story',
    subtasks: Array.isArray(item.subtasks)
      ? item.subtasks.map((st: any) => ({
          ...st,
          issueType: st.issue_type || st.issueType || 'Sub-task'
        }))
      : []
  }));
};

/**
 * Fetches details for a specific Jira issue by key.
 * GET /jira/issues/{issue_key}
 */
export const getIssueDetails = async (issueKey: string): Promise<JiraIssue> => {
  if (!issueKey) {
    throw new JiraApiError('Issue key is required', 400);
  }
  const data = await jiraFetch<any>(`/jira/issues/${encodeURIComponent(issueKey)}`);
  const raw = data?.issue || data?.story || data;
  return {
    ...raw,
    issueType: raw.issue_type || raw.issueType || 'Story',
    subtasks: Array.isArray(raw.subtasks)
      ? raw.subtasks.map((st: any) => ({
          ...st,
          issueType: st.issue_type || st.issueType || 'Sub-task'
        }))
      : []
  };
};

/**
 * Fetches comments for a specific Jira issue.
 * GET /jira/issues/{issue_key}/comments
 */
export const getIssueComments = async (issueKey: string): Promise<JiraComment[]> => {
  if (!issueKey) {
    throw new JiraApiError('Issue key is required', 400);
  }
  const data = await jiraFetch<any>(
    `/jira/issues/${encodeURIComponent(issueKey)}/comments`
  );

  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.comments)) {
    return data.comments;
  }
  return [];
};

/**
 * Diagnostic health check for Jira Cloud API integration.
 * GET /jira/health
 */
export const checkJiraHealth = async (): Promise<{ status: string; service?: string }> => {
  const url = `${JIRA_API_BASE_URL}/jira/health`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' }
  });

  if (!response.ok) {
    throw new JiraApiError('Jira health check failed', response.status);
  }

  try {
    return await response.json();
  } catch {
    return { status: 'ok', service: 'jira-api' };
  }
};
